import * as THREE from 'three'

/**
 * Objects tagged as helpers (grid, selection halo, ghost, bounds) are hidden for
 * the duration of a PNG export. The toggle has to be synchronous — the export
 * renders and reads back inside a single task, so React cannot re-render in
 * between — hence a scene-graph walk rather than state.
 */
export const HELPER_FLAG = 'isEditorHelper'

export interface HelperSnapshot {
  object: THREE.Object3D
  visible: boolean
}

export function hideHelpers(scene: THREE.Object3D): HelperSnapshot[] {
  const hidden: HelperSnapshot[] = []
  scene.traverse((object) => {
    if (object.userData[HELPER_FLAG]) {
      hidden.push({ object, visible: object.visible })
      object.visible = false
    }
  })
  return hidden
}

export function restoreHelpers(snapshot: HelperSnapshot[]): void {
  for (const entry of snapshot) entry.object.visible = entry.visible
}

export const helperProps = { userData: { [HELPER_FLAG]: true } } as const

/** True when the object or any ancestor is tagged as an editor helper. */
export function isHelper(object: THREE.Object3D | null): boolean {
  let current: THREE.Object3D | null = object
  while (current) {
    if (current.userData[HELPER_FLAG]) return true
    current = current.parent
  }
  return false
}

/**
 * World-space bounds of everything the camera should frame, one box per mesh.
 *
 * Per-mesh boxes matter: at a 45° azimuth the extreme corners of a single union
 * AABB are empty diagonal tips, and fitting to those zooms far too far out.
 * Contour lines (`outline: false`) and the blurred shadow plane opt out.
 */
export function sceneBoxes(
  root: THREE.Object3D,
  skip: (object: THREE.Object3D) => boolean,
): THREE.Box3[] {
  const boxes: THREE.Box3[] = []
  root.updateWorldMatrix(true, true)
  root.traverse((object) => {
    const mesh = object as THREE.Mesh
    if (!mesh.isMesh || !mesh.geometry) return
    if (mesh.userData.outline === false) return
    if (skip(object)) return
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox()
    const box = mesh.geometry.boundingBox
    if (!box) return
    boxes.push(box.clone().applyMatrix4(mesh.matrixWorld))
  })
  return boxes
}
